import { useState, useRef, useEffect } from 'react';
import { importUDDF, importOceanic, getUsers, createUser, deleteUser, resetPassword, getShareToken, regenerateShareToken, getBuddies, createBuddy, deleteBuddy, uploadBuddyPhoto, deleteBuddyPhoto, uploadBuddyHandtekening, deleteBuddyHandtekening, getVisibility, createVisibility, deleteVisibility, getLookup, createLookupItem, deleteLookupItem, getAppSetting, setAppSetting, getSpeciesStats, listBackups, createBackup, restoreBackup, deleteBackup, listGear, createGear, updateGear, deleteGear } from '../api';

function UploadCard({ title, icon, description, hint, onImport, accept }) {
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await onImport(file);
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="card">
      <div className="card-title">{icon} {title}</div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 13 }}>
        {description}
      </p>

      <div
        className={`upload-zone${dragOver ? ' drag-over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        <div className="upload-icon">📂</div>
        {loading ? (
          <p className="upload-text">Bestand wordt verwerkt…</p>
        ) : (
          <>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Sleep een bestand hierheen</p>
            <p className="upload-text">{hint}</p>
          </>
        )}
      </div>

      {result && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13 }}>
          ✅ <strong>{result.message}</strong>
          {result.errors?.length > 0 && (
            <ul style={{ marginTop: 8, color: 'var(--danger)', paddingLeft: 18 }}>
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div className="error" style={{ marginTop: 16 }}>❌ {error}</div>
      )}
    </div>
  );
}

function FotoAIPanel() {
  const [threshold, setThreshold] = useState(60);
  const [draft, setDraft]         = useState(60);
  const [stats, setStats]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  useEffect(() => {
    getAppSetting('species_min_confidence').then(r => {
      const v = parseInt(r.value) || 60;
      setThreshold(v);
      setDraft(v);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    getSpeciesStats(draft).then(setStats).catch(() => {});
  }, [draft]);

  const save = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await setAppSetting('species_min_confidence', draft);
      setThreshold(draft);
      setSaveMsg({ ok: true, text: '✓ Opgeslagen' });
    } catch (err) {
      setSaveMsg({ ok: false, text: '❌ ' + (err.response?.data?.error || err.message) });
    } finally {
      setSaving(false);
    }
  };

  const total = stats?.total || 0;
  const above = stats?.above || 0;
  const below = stats?.below || 0;

  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <div className="card-title">📸 Foto-AI instellingen</div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
          Score drempel — toon alleen herkenningen met zekerheid ≥
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input
            type="range" min={0} max={100} step={5}
            value={draft}
            onChange={e => setDraft(parseInt(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--blue)' }}
          />
          <span style={{ fontWeight: 700, fontSize: 20, minWidth: 48, textAlign: 'right', color: 'var(--blue)' }}>
            {draft}%
          </span>
        </div>
      </div>

      {/* Statistieken */}
      {stats && total > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
          <div style={{ textAlign: 'center', padding: '12px 8px', borderRadius: 10, background: '#f0fdf4' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>{above}</div>
            <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>boven drempel</div>
          </div>
          <div style={{ textAlign: 'center', padding: '12px 8px', borderRadius: 10, background: '#fef2f2' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626' }}>{below}</div>
            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>onder drempel</div>
          </div>
          <div style={{ textAlign: 'center', padding: '12px 8px', borderRadius: 10, background: 'var(--blue-light)' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)' }}>{stats.avg_confidence}%</div>
            <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 2 }}>gem. score</div>
          </div>
        </div>
      )}

      {stats && total === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
          Nog geen soorten herkend.
        </div>
      )}

      {/* Voortgangsbalk */}
      {stats && total > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>0%</span>
            <span>{draft}% drempel</span>
            <span>100%</span>
          </div>
          <div style={{ height: 8, borderRadius: 99, background: 'var(--border)', position: 'relative', overflow: 'hidden' }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${total ? Math.round((above / total) * 100) : 0}%`,
              background: 'linear-gradient(90deg, #16a34a, #4ade80)',
              borderRadius: 99,
              transition: 'width .3s',
            }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
            {total ? Math.round((above / total) * 100) : 0}% van alle {total} herkenningen boven de drempel
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Opslaan…' : '💾 Opslaan'}
        </button>
        {saveMsg && (
          <span style={{ fontSize: 12, color: saveMsg.ok ? '#16a34a' : '#dc2626' }}>
            {saveMsg.text}
          </span>
        )}
      </div>
    </div>
  );
}

function BackupPanel() {
  const [backups,    setBackups]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(false);
  const [restoring,  setRestoring]  = useState(null);  // filename dat hersteld wordt
  const [confirm,    setConfirm]    = useState(null);  // filename om te bevestigen
  const [msg,        setMsg]        = useState(null);  // { type: 'ok'|'err', text }

  const load = () => {
    setLoading(true);
    listBackups().then(setBackups).catch(() => setBackups([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    setMsg(null);
    try {
      await createBackup();
      setMsg({ type: 'ok', text: 'Backup aangemaakt.' });
      load();
    } catch (e) {
      setMsg({ type: 'err', text: 'Backup mislukt: ' + (e.response?.data?.error || e.message) });
    } finally {
      setCreating(false); }
  };

  const handleRestore = async (filename) => {
    setConfirm(null);
    setRestoring(filename);
    setMsg(null);
    try {
      await restoreBackup(filename);
      setMsg({ type: 'ok', text: `Database hersteld vanuit ${filename}.` });
    } catch (e) {
      setMsg({ type: 'err', text: 'Herstel mislukt: ' + (e.response?.data?.error || e.message) });
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (filename) => {
    try {
      await deleteBackup(filename);
      load();
    } catch (e) {
      setMsg({ type: 'err', text: 'Verwijderen mislukt: ' + (e.response?.data?.error || e.message) });
    }
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="card">
      <div className="card-title">🗄️ Database backup</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Backups worden opgeslagen op de server. Je kan een eerdere versie terugzetten zonder bestanden te verliezen.
      </p>

      {/* Aanmaken */}
      <button
        onClick={handleCreate}
        disabled={creating}
        style={{
          padding: '9px 20px', borderRadius: 8, border: 'none', fontWeight: 600,
          fontSize: 13, cursor: creating ? 'default' : 'pointer',
          background: creating ? 'var(--border)' : 'var(--blue)',
          color: creating ? 'var(--text-muted)' : '#fff',
          marginBottom: 20,
        }}>
        {creating ? '⏳ Backup aanmaken…' : '💾 Nieuwe backup aanmaken'}
      </button>

      {/* Feedback */}
      {msg && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${msg.type === 'ok' ? '#86efac' : '#fca5a5'}`,
          color: msg.type === 'ok' ? '#166534' : '#991b1b',
        }}>
          {msg.type === 'ok' ? '✅' : '❌'} {msg.text}
        </div>
      )}

      {/* Lijst */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>⏳ Laden…</div>
      ) : backups.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Nog geen backups beschikbaar.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {backups.map(b => (
            <div key={b.filename} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8,
              background: 'var(--card)', border: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(b.created_at)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fmtSize(b.size)}</div>
              </div>

              {confirm === b.filename ? (
                /* Bevestiging herstellen */
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Zeker?</span>
                  <button onClick={() => handleRestore(b.filename)} disabled={!!restoring}
                    style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {restoring === b.filename ? '⏳' : 'Ja, herstel'}
                  </button>
                  <button onClick={() => setConfirm(null)}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>
                    Annuleer
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setConfirm(b.filename)} disabled={!!restoring}
                    title="Herstel deze backup"
                    style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--blue-light)', color: 'var(--blue)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    ↩️ Herstel
                  </button>
                  <button onClick={() => handleDelete(b.filename)}
                    title="Verwijder backup"
                    style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: 'rgba(239,68,68,.12)', color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── GearPanel ─────────────────────────────────────────────────────────────────

const GEAR_TYPES = ['Regs', 'Computer', 'Droogpak', 'Wetsuit', 'Fles', 'Andere'];

const EMPTY_FORM = { name: '', type: '', brand: '', purchase_date: '', last_service_date: '', service_interval_months: 12, notes: '' };

function serviceStatus(item) {
  if (!item.last_service_date) return { color: '#f59e0b', label: 'Nooit' };
  const lastDate   = new Date(item.last_service_date);
  const now        = new Date();
  const monthsDiff = (now.getFullYear() - lastDate.getFullYear()) * 12 + (now.getMonth() - lastDate.getMonth());
  const interval   = item.service_interval_months || 12;
  if (monthsDiff > interval)               return { color: '#ef4444', label: 'Vervallen' };
  if (monthsDiff > interval * 0.75)        return { color: '#f59e0b', label: 'Bijna' };
  return { color: '#16a34a', label: 'OK' };
}

function GearPanel() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null);   // null = nieuw, number = bewerken
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  const load = () => {
    setLoading(true);
    listGear().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      name:                    item.name || '',
      type:                    item.type || '',
      brand:                   item.brand || '',
      purchase_date:           item.purchase_date ? String(item.purchase_date).substring(0, 10) : '',
      last_service_date:       item.last_service_date ? String(item.last_service_date).substring(0, 10) : '',
      service_interval_months: item.service_interval_months ?? 12,
      notes:                   item.notes || '',
    });
    setError(null);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditId(null); setError(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_date:     form.purchase_date || null,
        last_service_date: form.last_service_date || null,
        service_interval_months: parseInt(form.service_interval_months) || 12,
      };
      if (editId) {
        await updateGear(editId, payload);
      } else {
        await createGear(payload);
      }
      cancelForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" verwijderen?`)) return;
    try {
      await deleteGear(item.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const fmtDateShort = (d) => {
    if (!d) return '—';
    const dt = new Date(String(d).substring(0, 10));
    return `${dt.getDate()} ${['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'][dt.getMonth()]} ${dt.getFullYear()}`;
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>🔧 Uitrusting</div>
        {!showForm && (
          <button onClick={openNew} className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}>
            + Item toevoegen
          </button>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: 13, color: '#991b1b' }}>
          ❌ {error}
        </div>
      )}

      {/* Formulier */}
      {showForm && (
        <form onSubmit={handleSave} style={{
          background: 'var(--surface)', borderRadius: 10, padding: '16px 18px',
          border: '1px solid var(--border)', marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 12 }}>
            {editId ? 'Item bewerken' : 'Nieuw item'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Naam *</label>
              <input
                required
                placeholder="bv. Regulateur"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Type</label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
              >
                <option value="">— kies type —</option>
                {GEAR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Merk</label>
              <input
                placeholder="bv. Apeks"
                value={form.brand}
                onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Aankoopdatum</label>
              <input
                type="date"
                value={form.purchase_date}
                onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Laatste service</label>
              <input
                type="date"
                value={form.last_service_date}
                onChange={e => setForm(f => ({ ...f, last_service_date: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Service-interval (maanden)</label>
              <input
                type="number"
                min={1}
                max={120}
                value={form.service_interval_months}
                onChange={e => setForm(f => ({ ...f, service_interval_months: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Notities</label>
            <textarea
              placeholder="Optionele opmerkingen…"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={saving} className="btn btn-primary" style={{ fontSize: 12, padding: '7px 16px' }}>
              {saving ? 'Opslaan…' : (editId ? '💾 Opslaan' : '+ Toevoegen')}
            </button>
            <button type="button" onClick={cancelForm} className="btn btn-outline" style={{ fontSize: 12, padding: '7px 14px' }}>
              Annuleer
            </button>
          </div>
        </form>
      )}

      {/* Lijst */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Laden…</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Nog geen uitrusting toegevoegd.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(item => {
            const status = serviceStatus(item);
            return (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                background: 'var(--surface)', border: '1px solid var(--border)',
              }}>
                {/* Status dot */}
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: status.color, flexShrink: 0,
                  boxShadow: `0 0 0 3px ${status.color}22`,
                }} title={status.label} />

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    {item.name}
                    {item.brand && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>{item.brand}</span>}
                    {item.type  && <span style={{
                      marginLeft: 8, fontSize: 10, background: 'var(--blue-light)', color: 'var(--blue)',
                      padding: '1px 7px', borderRadius: 99, fontWeight: 700,
                    }}>{item.type}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    Service: <span style={{ color: status.color, fontWeight: 600 }}>{status.label}</span>
                    {item.last_service_date && <span style={{ marginLeft: 8 }}>· {fmtDateShort(item.last_service_date)}</span>}
                    <span style={{ marginLeft: 8 }}>· interval {item.service_interval_months} mnd</span>
                  </div>
                </div>

                {/* Acties */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => openEdit(item)}
                    title="Bewerken"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px', borderRadius: 6, color: 'var(--text-muted)' }}
                  >✏️</button>
                  <button
                    onClick={() => handleDelete(item)}
                    title="Verwijderen"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px', borderRadius: 6, color: 'var(--text-muted)' }}
                  >🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ImportPage() {
  const [activeTab, setActiveTab] = useState('importeren');

  const tabs = [
    { key: 'importeren',  label: '📥 Importeren' },
    { key: 'duikgegevens', label: '🤿 Duikgegevens' },
    { key: 'fotoai',      label: '📸 Foto-AI' },
    { key: 'gebruikers',  label: '👥 Gebruikers' },
    { key: 'delen',       label: '🔗 Delen' },
    { key: 'backup',      label: '🗄️ Backup' },
    { key: 'uitrusting',  label: '🔧 Uitrusting' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Instellingen</h1>
        <p className="page-subtitle">Importeer duiken, beheer gebruikers en deel je logboek</p>
      </div>

      {/* Tab balk met scroll-indicator */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <div style={{
          display: 'flex', gap: 2,
          borderBottom: '2px solid var(--border)',
          overflowX: 'auto', scrollbarWidth: 'none',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? 'var(--blue)' : 'var(--text-muted)',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color .15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Scroll-indicator rechts */}
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 2, width: 36, pointerEvents: 'none',
          background: 'linear-gradient(to right, transparent, var(--card) 80%)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4,
        }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', opacity: 0.6 }}>›</span>
        </div>
      </div>

      {/* Tab: Importeren */}
      {activeTab === 'importeren' && (
        <>
          <UploadCard
            title="Shearwater UDDF importeren"
            icon="🤿"
            accept=".uddf,.xml"
            description={
              <>
                Exporteer je duiken vanuit <strong>Shearwater Cloud Desktop</strong> als UDDF-bestand en upload het hier.
                Duplicaten worden automatisch overgeslagen.
              </>
            }
            hint="klik om een bestand te kiezen (.uddf of .xml)"
            onImport={importUDDF}
          />

          <UploadCard
            title="Oceanic+ importeren"
            icon="🍎"
            accept=".uddf,.xml"
            description={
              <>
                Exporteer je duiken vanuit de <strong>Oceanic+ app</strong> als UDDF-bestand.
                GPS-coördinaten worden automatisch gekoppeld aan je bestaande Shearwater-duiken
                op basis van datum en tijd (±30 minuten). Duiken zonder overeenkomst worden apart opgeslagen.
              </>
            }
            hint="klik om een Oceanic+ UDDF-bestand te kiezen"
            onImport={importOceanic}
          />

        </>
      )}

      {/* Tab: Duikgegevens */}
      {activeTab === 'duikgegevens' && (
        <>
          <BuddyManager />
          <VisibilityManager />
          <LookupManager category="problemen"  title="⚠️ Problemen"    hint="bv. Stroming, Materiaal pech, Dekompressie" />
          <LookupManager category="soort"      title="🤿 Soort duik"   hint="bv. Driftduik, Kantduik, Tender, Nacht" />
          <LookupManager category="reis"       title="✈️ Reis"         hint="bv. Groepsreis, Daguitstap, Vakantie" />
          <LookupManager category="thema"      title="🎯 Thema"        hint="bv. Proeven, Fundive, Fotografie" />
          <LookupManager category="soort_fles" title="🔵 Soort fles"   hint="bv. Staal, Aluminium, Carbon" />
          <LookupManager category="inspanning" title="💪 Inspanning"   hint="bv. Beperkt, Matig, Gewoon, Extreem" />
          <LookupManager category="pak"               title="🤿 Pak"              hint="bv. Droogpak, 7mm, 5mm, 3mm, Korte broek" />
          <LookupManager category="brevet_kwalificatie" title="🎓 Brevet kwalificatie" hint="bv. D1, D2, D3, I1, I2, I3" />
          <LookupManager category="verzekering_type"   title="🛡️ Verzekering type"   hint="bv. DAN, Duikverzekering, BA, Reisverzekering" />
        </>
      )}

      {/* Tab: Foto-AI */}
      {activeTab === 'fotoai' && <FotoAIPanel />}

      {/* Tab: Gebruikers */}
      {activeTab === 'gebruikers' && <UserManager />}

      {/* Tab: Delen */}
      {activeTab === 'delen' && <SharePanel />}

      {/* Tab: Backup */}
      {activeTab === 'backup' && <BackupPanel />}

      {/* Tab: Uitrusting */}
      {activeTab === 'uitrusting' && <GearPanel />}
    </div>
  );
}

function SharePanel() {
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);
  const [regen, setRegen]     = useState(false);

  const load = () => getShareToken().then(d => setToken(d.token)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const shareUrl = token
    ? `${window.location.origin}/share/${token}`
    : null;

  const qrUrl = shareUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}`
    : null;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Nieuw token aanmaken? De oude link werkt dan niet meer.')) return;
    setRegen(true);
    try {
      const d = await regenerateShareToken();
      setToken(d.token);
    } finally {
      setRegen(false);
    }
  };

  return (
    <div className="card">
      <div className="card-title">🔗 Publiek logboek delen</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Deel je duiklogboek zonder inloggen — handig om te tonen aan duikscholen of instructeurs ter plaatse.
        De pagina is alleen leesbaar en bevat geen persoonlijke gegevens.
      </p>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Laden…</div>
      ) : token ? (
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* QR code */}
          <div style={{ textAlign: 'center' }}>
            <img
              src={qrUrl}
              alt="QR code"
              style={{ width: 160, height: 160, borderRadius: 12, border: '1px solid var(--border)' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Scan om te openen</div>
          </div>

          {/* Link en acties */}
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 8 }}>
              Deelbare link
            </div>
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 12,
              wordBreak: 'break-all',
              color: 'var(--blue)',
              marginBottom: 12,
              fontFamily: 'monospace',
            }}>
              {shareUrl}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={handleCopy} className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}>
                {copied ? '✅ Gekopieerd!' : '📋 Kopieer link'}
              </button>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer"
                className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px', textDecoration: 'none' }}>
                🔍 Bekijken
              </a>
              <button onClick={handleRegenerate} disabled={regen}
                className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px', color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                {regen ? 'Bezig…' : '🔄 Nieuw token'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
              ⚠️ Bij een nieuw token worden alle bestaande links ongeldig.
            </div>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
            Er is nog geen share token aangemaakt.
          </p>
          <button onClick={handleRegenerate} className="btn btn-primary" disabled={regen}>
            {regen ? 'Aanmaken…' : '🔗 Token aanmaken'}
          </button>
        </div>
      )}
    </div>
  );
}

function UserManager() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newName, setNewName]   = useState('');
  const [newPass, setNewPass]   = useState('');
  const [newRole, setNewRole]   = useState('guest');
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  const load = () => getUsers().then(setUsers).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    try {
      await createUser({ username: newName, password: newPass, role: newRole });
      setNewName(''); setNewPass(''); setNewRole('guest');
      setSuccess(`Gebruiker "${newName}" aangemaakt`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Aanmaken mislukt');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Gebruiker "${u.username}" verwijderen?`)) return;
    try { await deleteUser(u.id); load(); }
    catch (err) { setError(err.response?.data?.error || 'Verwijderen mislukt'); }
  };

  const handleReset = async (u) => {
    const pw = window.prompt(`Nieuw wachtwoord voor "${u.username}":`);
    if (!pw) return;
    try { await resetPassword(u.id, pw); setSuccess('Wachtwoord gewijzigd'); }
    catch (err) { setError(err.response?.data?.error || 'Wijzigen mislukt'); }
  };

  return (
    <div className="card">
      <div className="card-title">👥 Gebruikersbeheer</div>

      {error   && <div className="error" style={{ marginBottom: 12 }}>❌ {error}</div>}
      {success && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>✅ {success}</div>}

      {/* Huidige gebruikers */}
      {!loading && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
          <thead>
            <tr>
              {['Gebruiker', 'Rol', 'Laatste login', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ padding: '10px 10px' }}>
                  <strong>{u.username}</strong>
                  {!u.active && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--danger)' }}>inactief</span>}
                </td>
                <td style={{ padding: '10px 10px' }}>
                  <span style={{ background: u.role === 'admin' ? 'var(--blue-light)' : '#f3f4f6', color: u.role === 'admin' ? 'var(--blue)' : 'var(--text-muted)', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                    {u.role}
                  </span>
                </td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)', fontSize: 12 }}>
                  {u.last_login ? new Date(u.last_login).toLocaleDateString('nl') : 'Nog nooit'}
                </td>
                <td style={{ padding: '10px 10px', display: 'flex', gap: 8 }}>
                  <button onClick={() => handleReset(u)} className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 11 }}>🔑 Reset</button>
                  <button onClick={() => handleDelete(u)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 11 }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Nieuwe gebruiker */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 12 }}>Nieuwe gebruiker toevoegen</div>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <input
            placeholder="Gebruikersnaam"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            required
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, flex: '1 1 140px' }}
          />
          <input
            placeholder="Wachtwoord"
            type="password"
            value={newPass}
            onChange={e => setNewPass(e.target.value)}
            required
            minLength={6}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, flex: '1 1 140px' }}
          />
          <select
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
          >
            <option value="guest">Gast</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" className="btn btn-primary">+ Toevoegen</button>
        </form>
      </div>
    </div>
  );
}

// ── BuddyManager ─────────────────────────────────────────────────────────────

function BuddyManager() {
  const [buddies, setBuddies]   = useState([]);
  const [newName, setNewName]   = useState('');
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(null); // `${id}-photo` of `${id}-handtekening`
  const fileRefs = useRef({});

  const load = () => getBuddies().then(setBuddies).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newName.trim()) return;
    try {
      const buddy = await createBuddy(newName.trim());
      setBuddies(prev => [...prev, buddy].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Toevoegen mislukt');
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`"${b.name}" verwijderen?`)) return;
    await deleteBuddy(b.id);
    setBuddies(prev => prev.filter(x => x.id !== b.id));
  };

  const handlePhotoUpload = async (b, file) => {
    if (!file) return;
    setUploading(`${b.id}-photo`);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const result = await uploadBuddyPhoto(b.id, fd);
      setBuddies(prev => prev.map(x => x.id === b.id ? { ...x, photo: result.photo } : x));
    } catch (err) {
      setError(err.response?.data?.error || 'Foto uploaden mislukt');
    }
    setUploading(null);
  };

  const handlePhotoDelete = async (b) => {
    await deleteBuddyPhoto(b.id);
    setBuddies(prev => prev.map(x => x.id === b.id ? { ...x, photo: null } : x));
  };

  const handleHandtekeningUpload = async (b, file) => {
    if (!file) return;
    setUploading(`${b.id}-handtekening`);
    try {
      const fd = new FormData();
      fd.append('handtekening', file);
      const result = await uploadBuddyHandtekening(b.id, fd);
      setBuddies(prev => prev.map(x => x.id === b.id ? { ...x, handtekening: result.handtekening } : x));
    } catch (err) {
      setError(err.response?.data?.error || 'Handtekening uploaden mislukt');
    }
    setUploading(null);
  };

  const handleHandtekeningDelete = async (b) => {
    await deleteBuddyHandtekening(b.id);
    setBuddies(prev => prev.map(x => x.id === b.id ? { ...x, handtekening: null } : x));
  };

  const buddyUrl = (filename) => `/uploads/buddies/${filename}.jpg`;

  return (
    <div className="card">
      <div className="card-title">👤 Buddy's</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
        Beheer je lijst van duikpartners. Je kan per buddy een profielfoto toevoegen.
      </p>

      {error && <div className="error" style={{ marginBottom: 12 }}>❌ {error}</div>}

      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {buddies.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nog geen buddy's toegevoegd.</span>
          )}
          {buddies.map(b => (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 12px',
              background: 'var(--surface)', borderRadius: 10,
              border: '1px solid var(--border)',
            }}>
              {/* Avatar */}
              <div
                onClick={() => fileRefs.current[`${b.id}-photo`]?.click()}
                title={b.photo ? 'Foto wijzigen' : 'Foto toevoegen'}
                style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  overflow: 'hidden', cursor: 'pointer', position: 'relative',
                  background: 'var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--border)',
                }}
              >
                {b.photo ? (
                  <img src={buddyUrl(b.photo)} alt={b.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 20, color: 'var(--text-muted)' }}>👤</span>
                )}
                {uploading === `${b.id}-photo` && (
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>⏳</div>
                )}
              </div>
              <input
                ref={el => fileRefs.current[`${b.id}-photo`] = el}
                type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => { handlePhotoUpload(b, e.target.files[0]); e.target.value = ''; }}
              />
              <input
                ref={el => fileRefs.current[`${b.id}-handtekening`] = el}
                type="file" accept="image/*"
                style={{ display: 'none' }}
                onChange={e => { handleHandtekeningUpload(b, e.target.files[0]); e.target.value = ''; }}
              />

              {/* Naam */}
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{b.name}</span>

              {/* Handtekening preview */}
              {b.handtekening && (
                <div
                  onClick={() => fileRefs.current[`${b.id}-handtekening`]?.click()}
                  title="Handtekening wijzigen"
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                >
                  <img
                    src={buddyUrl(b.handtekening)}
                    alt="handtekening"
                    style={{ height: 36, maxWidth: 110, borderRadius: 4, border: '1px solid var(--border)', objectFit: 'contain', background: '#fff', display: 'block' }}
                  />
                </div>
              )}

              {/* Foto-acties */}
              {b.photo ? (
                <button
                  onClick={() => handlePhotoDelete(b)}
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '4px 10px', color: 'var(--danger)' }}
                  title="Foto verwijderen"
                >🗑 Foto</button>
              ) : (
                <button
                  onClick={() => fileRefs.current[`${b.id}-photo`]?.click()}
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '4px 10px' }}
                >📷 Foto</button>
              )}

              {/* Handtekening-acties */}
              {b.handtekening ? (
                <button
                  onClick={() => handleHandtekeningDelete(b)}
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '4px 10px', color: 'var(--danger)' }}
                  title="Handtekening verwijderen"
                >🗑 Handtekening</button>
              ) : (
                <button
                  onClick={() => fileRefs.current[`${b.id}-handtekening`]?.click()}
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  title="Scan of foto van handtekening uploaden"
                >✍️ Handtekening</button>
              )}

              {/* Buddy verwijderen */}
              <button
                onClick={() => handleDelete(b)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, padding: '0 2px' }}
                title="Buddy verwijderen"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="Naam buddy…"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
        />
        <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px' }}>+ Toevoegen</button>
      </form>
    </div>
  );
}

// ── VisibilityManager ─────────────────────────────────────────────────────────

function VisibilityManager() {
  const [options, setOptions]   = useState([]);
  const [newVal, setNewVal]     = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(true);

  const load = () => getVisibility().then(setOptions).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newVal) return;
    try {
      const item = await createVisibility(parseFloat(newVal), newLabel.trim() || undefined);
      setOptions(prev => [...prev, item].sort((a, b) => a.value_m - b.value_m));
      setNewVal(''); setNewLabel('');
    } catch (err) {
      setError(err.response?.data?.error || 'Toevoegen mislukt');
    }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`${o.label} verwijderen?`)) return;
    await deleteVisibility(o.id);
    setOptions(prev => prev.filter(x => x.id !== o.id));
  };

  return (
    <div className="card">
      <div className="card-title">👁️ Zichtbaarheid</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
        Beheer de beschikbare zichtbaarheidswaarden (in meter) voor gebruik bij het bewerken van een duik.
      </p>

      {error && <div className="error" style={{ marginBottom: 12 }}>❌ {error}</div>}

      {!loading && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {options.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nog geen waarden toegevoegd.</span>
          )}
          {options.map(o => (
            <div key={o.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 99, padding: '5px 12px', fontSize: 13, fontWeight: 600,
            }}>
              {o.label}
              <button
                onClick={() => handleDelete(o)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 0, lineHeight: 1 }}
                title="Verwijderen"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Meters *</label>
          <input
            type="number"
            step="0.5"
            min="0"
            placeholder="bv. 10"
            value={newVal}
            onChange={e => setNewVal(e.target.value)}
            required
            style={{ width: 90, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Label (optioneel)</label>
          <input
            placeholder="bv. &lt; 1 m"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
          />
        </div>
        <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px' }}>+ Toevoegen</button>
      </form>
    </div>
  );
}

// ── Generieke LookupManager ───────────────────────────────────────────────────

function LookupManager({ category, title, hint }) {
  const [items, setItems]   = useState([]);
  const [newVal, setNewVal] = useState('');
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => getLookup(category).then(setItems).finally(() => setLoading(false));
  useEffect(() => { load(); }, [category]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newVal.trim()) return;
    try {
      const item = await createLookupItem(category, newVal.trim());
      setItems(prev => [...prev, item]);
      setNewVal('');
    } catch (err) {
      setError(err.response?.data?.error || 'Toevoegen mislukt');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.value}" verwijderen?`)) return;
    await deleteLookupItem(category, item.id);
    setItems(prev => prev.filter(x => x.id !== item.id));
  };

  return (
    <div className="card">
      <div className="card-title">{title}</div>
      {hint && <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>{hint}</p>}
      {error && <div className="error" style={{ marginBottom: 12 }}>❌ {error}</div>}
      {!loading && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {items.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nog geen waarden.</span>
          )}
          {items.map(item => (
            <div key={item.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 99, padding: '5px 12px', fontSize: 13, fontWeight: 500,
            }}>
              {item.value}
              <button onClick={() => handleDelete(item)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 14, padding: 0, lineHeight: 1,
              }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="Nieuwe waarde…"
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
        />
        <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px' }}>+ Toevoegen</button>
      </form>
    </div>
  );
}
